import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

type Options<T extends object> = {
	initialFilters: T;
	onApply: (filters: T) => void;
	textFilterKeys?: readonly (keyof T)[];
	debounceMs?: number;
};

export function useAutoApplyFilters<T extends object>({
	initialFilters,
	onApply,
	textFilterKeys = [],
	debounceMs = 300,
}: Options<T>) {
	const [filters, setFilters] = useState(initialFilters);
	const filtersRef = useRef(filters);
	const onApplyRef = useRef(onApply);
	const textFilterKeysRef = useRef(textFilterKeys);
	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const lastAppliedRef = useRef<string | undefined>(undefined);
	const initialFiltersSignature = JSON.stringify(initialFilters);
	const stableInitialFilters = useMemo(
		() => JSON.parse(initialFiltersSignature) as T,
		[initialFiltersSignature],
	);

	const cancelPendingApply = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = undefined;
		}
	}, []);

	useLayoutEffect(() => {
		onApplyRef.current = onApply;
		textFilterKeysRef.current = textFilterKeys;
	}, [onApply, textFilterKeys]);

	useLayoutEffect(() => {
		cancelPendingApply();
		filtersRef.current = stableInitialFilters;
		lastAppliedRef.current = initialFiltersSignature;
		setFilters(stableInitialFilters);
	}, [cancelPendingApply, initialFiltersSignature, stableInitialFilters]);

	useEffect(() => cancelPendingApply, [cancelPendingApply]);

	const apply = useCallback((next: T) => {
		const signature = JSON.stringify(next);
		if (signature === lastAppliedRef.current) return;
		lastAppliedRef.current = signature;
		onApplyRef.current(next);
	}, []);

	const onFiltersChange = useCallback(
		(patch: Partial<T>) => {
			const next = { ...filtersRef.current, ...patch };
			filtersRef.current = next;
			setFilters(next);
			cancelPendingApply();

			const debounce = textFilterKeysRef.current.some((key) => key in patch);
			if (debounce) {
				timerRef.current = setTimeout(() => apply(next), debounceMs);
			} else {
				apply(next);
			}
		},
		[apply, cancelPendingApply, debounceMs],
	);

	const clearFilters = useCallback(
		(cleared: T) => {
			cancelPendingApply();
			filtersRef.current = cleared;
			setFilters(cleared);
			apply(cleared);
		},
		[apply, cancelPendingApply],
	);

	return { filters, onFiltersChange, clearFilters };
}
